// Sync profils de rôle ↔ features contrat business

import {
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import {
  Business,
  BusinessFeatureKey,
  getActiveFeatures,
} from '../types/business';
import { normalizeRolePermissions, RolePermissions } from '../types/user';
import { PERMISSION_SECTIONS_FOR_DISPLAY, getPermissionGroupGatingFeatures } from '../features/role-profiles/components/RoleProfilePermissionsEditor';

/** Minimum de features actives pour créer / dupliquer un profil de rôle. */
export const MIN_ACTIVE_FEATURES_FOR_ROLE_PROFILES = 4;

/** Clés de permission rattachées à une feature (section + bloc). */
export const getPermissionKeysForFeature = (
  featureKey: BusinessFeatureKey
): (keyof RolePermissions)[] => {
  const keys = new Set<keyof RolePermissions>();
  for (const section of PERMISSION_SECTIONS_FOR_DISPLAY) {
    for (const group of section.groups) {
      const gating = getPermissionGroupGatingFeatures(section, group);
      if (gating.includes(featureKey)) {
        group.permissions.forEach((p) => keys.add(p.key));
      }
    }
  }
  return Array.from(keys);
};

export const getPermissionKeysForFeatures = (
  featureKeys: BusinessFeatureKey[]
): (keyof RolePermissions)[] => {
  const keys = new Set<keyof RolePermissions>();
  featureKeys.forEach((fk) => {
    getPermissionKeysForFeature(fk).forEach((k) => keys.add(k));
  });
  return Array.from(keys);
};

export const stripFeaturePermissions = (
  permissions: RolePermissions,
  featureKeys: BusinessFeatureKey[]
): RolePermissions => {
  const next = { ...normalizeRolePermissions(permissions) };
  const keys = getPermissionKeysForFeatures(featureKeys);
  keys.forEach((key) => {
    next[key] = false;
  });
  return next;
};

/** Sur save profil : force à false les droits des blocs dont une feature gating est inactive. */
export const stripInactiveFeaturePermissions = (
  permissions: RolePermissions,
  business: Business | null
): RolePermissions => {
  const active = new Set(getActiveFeatures(business));
  const next = { ...normalizeRolePermissions(permissions) };

  for (const section of PERMISSION_SECTIONS_FOR_DISPLAY) {
    for (const group of section.groups) {
      const gating = getPermissionGroupGatingFeatures(section, group);
      const inactive = gating.length === 0 || gating.some((fk) => !active.has(fk));
      if (inactive) {
        group.permissions.forEach((p) => {
          next[p.key] = false;
        });
      }
    }
  }

  return next;
};

export const canCreateRoleProfiles = (business: Business | null): boolean =>
  getActiveFeatures(business).length >= MIN_ACTIVE_FEATURES_FOR_ROLE_PROFILES;

export const getRoleProfilesFeatureGateMessage = (business: Business | null): string => {
  const count = getActiveFeatures(business).length;
  return `Au moins ${MIN_ACTIVE_FEATURES_FOR_ROLE_PROFILES} features doivent être actives sur ce business pour créer un profil de rôle (actuellement ${count}). Activez-les dans l’onglet Configuration.`;
};

/**
 * Retire des profils Firestore les permissions liées aux features désactivées.
 * @returns nombre de profils mis à jour
 */
export const stripDisabledFeaturesFromBusinessRoleProfiles = async (
  businessId: string,
  featureKeys: BusinessFeatureKey[]
): Promise<number> => {
  if (!businessId || featureKeys.length === 0) return 0;

  const permissionKeys = getPermissionKeysForFeatures(featureKeys);
  if (permissionKeys.length === 0) return 0;

  const q = query(
    collection(db, COLLECTIONS.roleProfiles),
    where('businessId', '==', businessId)
  );
  const snap = await getDocs(q);
  let updated = 0;

  for (const profileDoc of snap.docs) {
    const data = profileDoc.data();
    const current = normalizeRolePermissions(data.permissions);
    const hadAnyTrue = permissionKeys.some((k) => current[k] === true);
    if (!hadAnyTrue) continue;

    const next = stripFeaturePermissions(current, featureKeys);
    await updateDoc(doc(db, COLLECTIONS.roleProfiles, profileDoc.id), {
      permissions: next,
      updatedAt: Timestamp.now(),
    });
    updated += 1;
  }

  return updated;
};
